import React, { useState, useContext } from "react";
import { View, StyleSheet } from "react-native";
import NtText from "../../components/NtText";
import colors from "../../config/colors";
import { useTheme } from "../../context/ThemeContext";
import NtActivityIndicator from "../../components/NtActivityIndicator";
import NtTouchableEffect from "../../components/NtTouchableEffect";
import Logger from "../../common/utils/Logger";
import UserContext from "../../context/UserContext";
import NtModalUnactiveButton from "../../components/NtModalUnactiveButton";
import NtModalActiveButton from "../../components/NtModalActiveButton";
import {
  handleExtensionCreate,
  handleFailureFile,
} from "../utils/ExtensionUploadParser";
import NtModalTitle from "../../components/NtModalTitle";
import NtModalSubtitle from "../../components/NtModalSubtitle";

function NtExtensionUploadForm({ onCancel, onUploaded }) {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [uploadResponse, setUploadResponse] = useState(null);

  const context = useContext(UserContext);

  const handleUpload = async () => {
    setLoading(true);
    const response = await handleExtensionCreate(file);
    setLoading(false);
    setUploadResponse(response);
  };

  const handleDone = () => {
    if (onUploaded) {
      onUploaded(uploadResponse);
    }
  };

  const handleFailureDownload = async () => {
    const response = await handleFailureFile(uploadResponse.failure);
    const fileData = response.data;

    const blob = new Blob([fileData], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.download = "failure-uploaded-extensions.txt";
    link.href = url;
    link.click();
  };

  const isRequiredFields = () => {
    return file;
  };

  const changeHandler = async (event) => {
    setFile(event.target.files[0]);
  };

  const renderChooseCSV = () => {
    if (file) {
      return null;
    }
    return (
      <NtTouchableEffect
        style={{}}
        onPress={() => {
          document.getElementById("selectFile").click();
        }}
      >
        <NtText style={{ fontSize: 13, fontWeight: "400", color: colors.blue }}>
          Choose CSV file
        </NtText>
      </NtTouchableEffect>
    );
  };

  const renderInfoCSV = () => {
    if (!file) {
      return null;
    }
    return (
      <View>
        <NtText>{file?.name}</NtText>
      </View>
    );
  };

  const renderCancel = () => {
    if (uploadResponse) {
      return null;
    }
    return (
      <NtTouchableEffect style={styles.cancelButton} onPress={onCancel}>
        <NtText
          style={{ fontSize: 13, fontWeight: "600", color: colors.darkest }}
        >
          Cancel
        </NtText>
      </NtTouchableEffect>
    );
  };

  const renderUpload = (containerStyle) => {
    return (
      <NtTouchableEffect
        style={[
          styles.addButton,
          containerStyle,
          {
            backgroundColor: isRequiredFields() ? colors.blue : colors.lighGray,
          },
        ]}
        onPress={
          isRequiredFields()
            ? () => {
                if (uploadResponse) {
                  handleDone();
                } else {
                  handleUpload();
                }
              }
            : null
        }
      >
        <NtText style={{ fontSize: 13, fontWeight: "600", color: "white" }}>
          {uploadResponse ? "Done" : "Upload"}
        </NtText>
      </NtTouchableEffect>
    );
  };

  return (
    <View style={styles.container}>
      <NtModalTitle>Upload extensions csv</NtModalTitle>
      <NtModalSubtitle style={[{ marginTop: 4 }]}>
        This will allow to upload extension bulk. Columns MUST be named (case
        sensitive):{" "}
        <NtText style={[styles.subtitle, { fontWeight: "bold" }]}>phone</NtText>
        ,{" "}
        <NtText style={[styles.subtitle, { fontWeight: "bold" }]}>
          extension
        </NtText>
        ,{" "}
        <NtText style={[styles.subtitle, { fontWeight: "bold" }]}>
          password
        </NtText>
        ,{" "}
        <NtText style={[styles.subtitle, { fontWeight: "bold" }]}>name</NtText>,{" "}
        <NtText style={[styles.subtitle, { fontWeight: "bold" }]}>
          description
        </NtText>
        ,{" "}
        <NtText style={[styles.subtitle, { fontWeight: "bold" }]}>
          domain
        </NtText>
      </NtModalSubtitle>

      <View
        style={{
          borderRadius: 4,
          borderWidth: 1,
          borderColor: colors.lightest,
          backgroundColor: colors.lighestGray,
          justifyContent: "center",
          padding: 10,
          marginTop: 30,
        }}
      >
        {renderChooseCSV()}
        {renderInfoCSV()}
      </View>
      {uploadResponse && (
        <View
          style={{
            marginTop: 6,
          }}
        >
          <NtText style={[styles.uploadResponseText]}>
            Total: {uploadResponse?.total}
          </NtText>
          <NtText style={[styles.uploadResponseText, { marginTop: 4 }]}>
            Success: {uploadResponse?.success?.length}
          </NtText>
          <NtTouchableEffect
            style={{ marginTop: 4 }}
            onPress={() => {
              if (uploadResponse?.failure?.length > 0) {
                handleFailureDownload();
              }
            }}
          >
            <NtText style={[styles.uploadResponseText, { color: theme.red }]}>
              Error: {uploadResponse?.failure?.length}
            </NtText>
          </NtTouchableEffect>
        </View>
      )}

      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "flex-end",
          marginTop: 25,
        }}
      >
        {loading && (
          <NtActivityIndicator
            color={colors.blue}
            size="small"
            containerStyle={{ marginRight: 25 }}
          />
        )}
        <NtModalUnactiveButton title={"Cancel"} onPress={onCancel} />
        <NtModalActiveButton
          containerStyle={{ marginLeft: 15 }}
          title={uploadResponse ? "Done" : "Upload"}
          onPress={
            isRequiredFields()
              ? () => {
                  if (uploadResponse) {
                    handleDone();
                  } else {
                    handleUpload();
                  }
                }
              : null
          }
          enabled={isRequiredFields()}
        />
      </View>

      <input
        id="selectFile"
        type="file"
        name="file"
        accept=".csv"
        style={{ display: "none" }}
        onChange={changeHandler}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    minWidth: 500,
    maxWidth: 600,
    padding: 15,
  },
  cancelButton: {
    borderRadius: 4,
    borderWidth: 1,
    borderColor: colors.darkest,
    alignItems: "center",
    justifyContent: "center",
    paddingLeft: 8,
    paddingRight: 8,
    paddingTop: 4,
    paddingBottom: 4,
  },
  addButton: {
    borderRadius: 4,
    alignItems: "center",
    justifyContent: "center",
    paddingLeft: 8,
    paddingRight: 8,
    paddingTop: 4,
    paddingBottom: 4,
    backgroundColor: colors.lighGray,
  },
  uploadResponseText: {
    fontSize: 13,
    fontWeight: "600",
    color: colors.darkest,
  },
});

export default NtExtensionUploadForm;
